import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const TARGETS = [["active", "Active players", "played in the last 7 days"], ["all", "Every player", "all real accounts with a wolf"], ["one", "One player", "by email"]];

export default function AdminGifts() {
  const [target, setTarget] = useState("active");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("1000");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [later, setLater] = useState(false);
  const [when, setWhen] = useState("");
  const [sched, setSched] = useState([]);
  const loadSched = () => api.get("/admin/gifts/scheduled").then(({ data }) => setSched(data)).catch(() => {});
  const load = () => api.get("/admin/gifts").then(({ data }) => setHistory(data)).catch((e) => { toast.error(errMsg(e)); setHistory([]); });
  useEffect(() => { load(); loadSched(); }, []);
  const cancel = async (g) => {
    if (!window.confirm("Cancel this scheduled gift?")) return;
    try { await api.delete(`/admin/gifts/scheduled/${g.gift_id}`); toast("Scheduled gift cancelled"); loadSched(); } catch (e) { toast.error(errMsg(e)); }
  };

  const review = async (e) => {
    e.preventDefault();
    if (!(+amount > 0)) return toast.error("Enter an amount above 0");
    if (target === "one" && !email.trim()) return toast.error("Enter the player's email");
    if (later && (!when || new Date(when) <= new Date())) return toast.error("Pick a date and time in the future");
    try {
      const { data } = await api.get("/admin/gift/preview", { params: { target, email: target === "one" ? email : undefined } });
      if (!data.count) return toast.error("No players match");
      setPreview({ ...data, giftId: crypto.randomUUID() });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gift", { target, email: target === "one" ? email : null, amount: +amount, message: message || null,
        gift_id: preview.giftId, send_at: later ? new Date(when).toISOString() : null });
      toast.success(data.scheduled ? `Gift scheduled for ${new Date(data.send_at).toLocaleString()}` : `Sent ${(+amount).toLocaleString()} credits to ${data.count} player(s)`);
      setPreview(null); setMessage(""); setLater(false); setWhen(""); load(); loadSched();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <form onSubmit={review} className="glass rounded-2xl p-6 space-y-4" data-testid="gift-form">
        <h3 className="font-display text-xl text-amber-100">Send credits</h3>
        <div className="space-y-2">
          {TARGETS.map(([k, l, d]) => (
            <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${target === k ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-slate-700"}`}>
              <input type="radio" name="target" data-testid={`gift-target-${k}`} checked={target === k} onChange={() => setTarget(k)} className="accent-fuchsia-500" />
              <span>{l} <span className="text-slate-500 text-xs">· {d}</span></span>
            </label>
          ))}
        </div>
        {target === "one" && <Input data-testid="gift-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@email.com" className="bg-slate-900" />}
        <Input data-testid="gift-amount-input" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} placeholder="Credits per player" className="bg-slate-900" />
        <Input data-testid="gift-message-input" value={message} maxLength={200} onChange={(e) => setMessage(e.target.value)} placeholder="Optional message shown in the popup" className="bg-slate-900" />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="gift-schedule-toggle" checked={later} onChange={(e) => setLater(e.target.checked)} className="accent-fuchsia-500" />
          Schedule for later
        </label>
        {later && <Input data-testid="gift-schedule-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-slate-900" />}
        <button data-testid="gift-review-btn" className="ember-btn w-full py-3 rounded-lg text-sm">REVIEW GIFT</button>
        <p className="text-[11px] text-slate-500">Admin and test accounts are skipped for group gifts. Players see a popup the next time they open the game.</p>
      </form>
      <div className="space-y-6">
      <div className="glass rounded-2xl p-6" data-testid="gift-scheduled">
        <h3 className="font-display text-xl text-amber-100 mb-1">Scheduled gifts</h3>
        <p className="text-[11px] text-slate-500 mb-4">Sent within 15 minutes of the chosen time. Recipients are worked out when it sends.</p>
        {!sched.length && <p className="text-sm text-slate-500">Nothing scheduled.</p>}
        <div className="space-y-1.5">
          {sched.map((g) => (
            <div key={g.gift_id} data-testid={`gift-scheduled-${g.gift_id}`} className="flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-lg bg-slate-900/60">
              <span className="font-mono-game text-xs text-cyan-300">{new Date(g.send_at).toLocaleString()}</span>
              <span className="text-slate-300">{g.target === "one" ? g.email : g.target === "all" ? "Every player" : "Active players"}</span>
              <span className="text-slate-500">{g.amount.toLocaleString()} each</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${g.status === "scheduled" ? "bg-cyan-500/15 text-cyan-200" : g.status === "sent" ? "bg-emerald-500/15 text-emerald-200" : "bg-slate-800 text-slate-400"}`}>{g.status}</span>
              {g.status === "scheduled" && <button data-testid={`gift-cancel-scheduled-${g.gift_id}`} onClick={() => cancel(g)} className="text-xs text-red-300 hover:underline">Cancel</button>}
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-xl text-amber-100 mb-4">Gift history</h3>
        {!history && <p className="text-sm text-slate-500">Loading…</p>}
        {history?.length === 0 && <p className="text-sm text-slate-500">No gifts sent yet.</p>}
        <div className="space-y-1.5" data-testid="gift-history">
          {(history || []).map((g) => (
            <div key={g.gift_id} className="flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-lg bg-slate-900/60">
              <span className="font-mono-game text-xs text-slate-500">{g.created_at.slice(0, 16).replace("T", " ")}</span>
              <span className="text-slate-300">{g.target === "one" ? g.email : g.target === "all" ? "Every player" : "Active players"}</span>
              <span className="text-slate-500">{g.count} × {g.amount.toLocaleString()}</span>
              <span className="ml-auto font-mono-game text-amber-200">{g.total.toLocaleString()} total</span>
            </div>
          ))}
        </div>
      </div>
      </div>
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent data-testid="gift-confirm-dialog" className="glass">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{later ? "Schedule" : "Send"} {(+amount).toLocaleString()} credits to {preview?.count} player(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              {later ? `It will go out on ${when ? new Date(when).toLocaleString() : "?"} to whoever matches then (${preview?.count} today). You can cancel it before then.`
                : `That's ${((+amount) * (preview?.count || 0)).toLocaleString()} credits in total. This can't be undone from here.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-48 overflow-y-auto text-sm space-y-1" data-testid="gift-confirm-list">
            {preview?.recipients.map((r) => <p key={r.user_id} className="text-slate-300">{r.wolf_name || "No wolf yet"} <span className="text-slate-500">· {r.email}</span></p>)}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gift-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="gift-confirm-btn" disabled={busy} onClick={(e) => { e.preventDefault(); send(); }} className="ember-btn">{later ? "Schedule gift" : "Send gift"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
